/**
 * Local-first mutations of the ORGANIZATION layer (org_settings/campus/room).
 *
 * They write to the local PowerSync DB (Drizzle) → the sync queue sends them to the server
 * (`db/sync.rs`), which applies authz by capability (org_rbac: owner/org_admin for settings and
 * campuses; campus_admin over their own campus for rooms) and REJECTS-and-logs what's
 * unauthorized. The client is never the authority.
 */

import { campus, eq, org_settings, type PowerSyncDrizzleDB, room } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";

// ─── org_settings ─────────────────────────────────────────────────────────────

export interface OrgSettingsInput {
  country?: string;
  timezone?: string;
  locale?: string;
  currency_primary?: string;
  currency_secondary?: string | null;
  exchange_rate?: number | null;
  grading_scheme?: string;
  /** Group E: org's default grading model (NULL = legacy key). */
  grading_model_id?: string | null;
  attendance_alert_threshold?: number;
  // AUTONOMY: jsonb columns (travel as a JSON string via PowerSync).
  /** Org feature overrides (`{"<key>": boolean}`). */
  features?: string | null;
  /** Academy branding (`{"publicName": string, "tagline"?: string}`). */
  branding?: string | null;
}

/**
 * Saves (creates or updates) an organization's settings.
 *
 * CC-59 · **The contract is one of INTENT, not presence.** The keys that
 * appear in `input` are exactly the columns this write is opining on, and
 * they travel declared in `asserted_fields` (client-only column). The
 * write-path preserves the server's value for everything else.
 *
 * Why "send only what changed" isn't enough: PowerSync sends the ENTIRE LOCAL
 * row in a `PUT`, so omitted columns arrive just the same (as `null`) and the
 * server couldn't distinguish them from a deliberate wipe. That's why a form
 * painted before the row came down from the server would turn off all the
 * academy's features when saving the attendance threshold.
 *
 * `exists` indicates whether the singleton row is already local (the component knows
 * from its query): in that case it does an UPDATE (→ PATCH in sync); if not, INSERT (→
 * PUT). No code defaults are filled in the INSERT: the server sets them (COALESCE) for
 * whatever isn't asserted, so creating the row without opining on almost anything is a
 * legitimate, non-destructive operation.
 */
export async function saveOrgSettingsMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: OrgSettingsInput,
  exists: boolean,
): Promise<void> {
  const now = nowISO();
  // A key with `undefined` is NOT an opinion (it's the natural way to say
  // "I didn't touch this field" in TS), so it doesn't enter either the
  // values or the mask.
  const entries = Object.entries(input).filter(([, value]) => value !== undefined);
  const claimed = Object.fromEntries(entries) as OrgSettingsInput;
  const asserted_fields = JSON.stringify(entries.map(([key]) => key).toSorted());
  if (exists) {
    await patchRowVerified(db, org_settings, org_settings.id, orgId, {
      ...claimed,
      asserted_fields,
    });
    return;
  }
  await db.insert(org_settings).values({
    ...claimed,
    id: orgId,
    asserted_fields,
    created_at: now,
    updated_at: now,
  });
}

// ─── campus ───────────────────────────────────────────────────────────────────

export interface CampusInput {
  name?: string;
  slug?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  timezone?: string | null;
  status?: string;
}

/** Creates a campus. Returns its id. */
export async function createCampusMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CampusInput = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(campus).values({
    id,
    organization_id: orgId,
    name: input.name?.trim() || "Sede principal",
    slug: input.slug ?? null,
    address: input.address ?? null,
    city: input.city ?? null,
    state: input.state ?? null,
    phone: input.phone ?? null,
    timezone: input.timezone ?? null,
    status: input.status ?? "active",
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web` (no campus-edit UI). */
export async function updateCampusMutation(
  db: PowerSyncDrizzleDB,
  campusId: string,
  updates: CampusInput,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.name === "string") {
    patch.name = patch.name.trim() || "Sede principal";
  }
  await db
    .update(campus)
    .set({ ...patch, updated_at: nowISO() })
    .where(eq(campus.id, campusId));
}

/**
 * Soft delete of a campus. `organizationId`: the org the caller ALREADY has
 * in context (`CampusesTab`/`RoomsManager` — `props.orgId`, the one from the
 * organization route being managed). Deletion is `owner`/`org_admin`
 * (`org_rbac`, see the file header) — an organization capability, so
 * `organization_id` is the real boundary.
 */
export async function deleteCampusMutation(
  db: PowerSyncDrizzleDB,
  campusId: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    campus,
    campus.id,
    campusId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: campus.organization_id, value: organizationId }]),
  );
}

// ─── room ─────────────────────────────────────────────────────────────────────

export interface RoomInput {
  name?: string;
  capacity?: number;
  floor?: string | null;
}

/** Creates a room in a campus. Returns its id. */
export async function createRoomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  campusId: string,
  input: RoomInput = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(room).values({
    id,
    campus_id: campusId,
    organization_id: orgId,
    name: input.name?.trim() || "Aula",
    capacity: input.capacity ?? 0,
    floor: input.floor ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web` (no room-edit UI). */
export async function updateRoomMutation(
  db: PowerSyncDrizzleDB,
  roomId: string,
  updates: RoomInput,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.name === "string") {
    patch.name = patch.name.trim() || "Aula";
  }
  await db
    .update(room)
    .set({ ...patch, updated_at: nowISO() })
    .where(eq(room.id, roomId));
}

/** Soft delete of a room. `organizationId`: see note in `deleteCampusMutation`. */
export async function deleteRoomMutation(
  db: PowerSyncDrizzleDB,
  roomId: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    room,
    room.id,
    roomId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: room.organization_id, value: organizationId }]),
  );
}
